import type { InstanceActivityPayload } from "@arti/dashboard-events";

import { useEffect, useRef, useState } from "react";

import type { ChannelMessage, StreamEvent } from "./blackboardModels";

import { useDashboardRealtime } from "../../hooks/useDashboardRealtime";

// ---------------------------------------------------------------------------
// Hook: useInstanceStream — WS-based live instance activity
// Subscribes to internalEventTopic, filters instance_activity events by ID.
// ---------------------------------------------------------------------------

export type IngestedStreamEvent = StreamEvent & { ingestionSeq: number };

interface UseInstanceStreamResult {
  /** Live activity events (tool calls, phase transitions, etc.) */
  events: IngestedStreamEvent[];
  /** True when old events were dropped due to the 200-event cap. */
  eventsOverflowed: boolean;
  /** ISO timestamp captured when this stream subscription mounted. */
  liveSince: string | null;
  /** Chat messages received on channels */
  messages: ChannelMessage[];
  connected: boolean;
}

/** Safely coerce an unknown value to a string. Returns the fallback for non-string types. */
function str(value: unknown, fallback = ""): string {
  return typeof value === "string" ? value : fallback;
}

function num(value: unknown): number | null {
  return typeof value === "number" && Number.isFinite(value) ? value : null;
}

function activitySignal(
  activityType: string,
  label: string,
  message: string,
  timestamp: string,
): StreamEvent {
  return {
    activityType,
    id: crypto.randomUUID(),
    label,
    message,
    timestamp,
    type: "activity_signal",
  };
}

function withEventSource(
  event: StreamEvent,
  sourceInstanceId: string | null,
  channelId: string | null,
  ingestionSeq: number,
): IngestedStreamEvent {
  return {
    ...event,
    ingestionSeq,
    ...(channelId ? { channelId } : {}),
    ...(sourceInstanceId ? { sourceInstanceId } : {}),
  };
}

function cacheBreakReason(
  detail: InstanceActivityPayload["detail"],
): string | undefined {
  if (
    typeof detail === "object" &&
    detail !== null &&
    "break_reason" in detail &&
    typeof detail.break_reason === "string"
  ) {
    return detail.break_reason;
  }
  if (
    typeof detail === "object" &&
    detail !== null &&
    "reason" in detail &&
    typeof detail.reason === "string"
  ) {
    return detail.reason;
  }
  return undefined;
}

/**
 * Map an instance_activity payload to a StreamEvent shape.
 * The payload is a discriminated union on activity_type — the switch narrows
 * detail to the correct shape per case. Runtime guards (str/num) remain because
 * the WS client does not zod-parse incoming messages.
 *
 * Three former cases (tool_call, phase_transition, budget_warning) were
 * removed: they were never emitted as instance_activity (see the schema fix
 * in packages/dashboard-events). The StreamEvent union retains their types
 * for StreamTab's exhaustive switch, but this mapper never produces them.
 */
function mapActivityToStreamEvent(
  payload: InstanceActivityPayload,
  timestamp: number,
): StreamEvent | null {
  const id = crypto.randomUUID();
  const ts = new Date(timestamp).toISOString();

  switch (payload.activity_type) {
    case "channel_message": {
      const { detail } = payload;
      return {
        channelId: str(detail.channel_id),
        content: str(detail.content),
        id: str(detail.message_id) || id,
        senderType: str(detail.sender_type, "unknown"),
        timestamp: ts,
        type: "channel_message",
      };
    }
    case "cache_stats": {
      const { detail } = payload;
      return {
        cacheHitPct:
          typeof detail.cache_hit_pct === "number"
            ? detail.cache_hit_pct
            : null,
        cacheReadTokens:
          typeof detail.cache_read_tokens === "number"
            ? detail.cache_read_tokens
            : 0,
        id,
        isBreak: detail.is_break,
        // Producer emits break_reason (classified) and optionally reason
        // (input.reason). The break reason is what operators need.
        reason: cacheBreakReason(detail),
        timestamp: ts,
        type: "cache_stats",
      };
    }
    case "token_delta": {
      const { detail } = payload;
      return {
        cacheReadTokensDelta:
          typeof detail.cache_read_tokens_delta === "number"
            ? detail.cache_read_tokens_delta
            : 0,
        cacheWriteTokensDelta:
          typeof detail.cache_write_tokens_delta === "number"
            ? detail.cache_write_tokens_delta
            : 0,
        costDeltaUsd:
          typeof detail.cost_delta_usd === "number" ? detail.cost_delta_usd : 0,
        id,
        inputTokensDelta:
          typeof detail.input_tokens_delta === "number"
            ? detail.input_tokens_delta
            : 0,
        outputTokensDelta:
          typeof detail.output_tokens_delta === "number"
            ? detail.output_tokens_delta
            : 0,
        timestamp: ts,
        type: "token_delta",
      };
    }
    case "status_change": {
      const { detail } = payload;
      return {
        id,
        status: str(detail.status),
        timestamp: ts,
        type: "status_change",
      };
    }
    case "actor_wake": {
      return activitySignal(
        payload.activity_type,
        "WAKE",
        str(payload.detail.trigger, "actor activation started"),
        ts,
      );
    }
    case "actor_park": {
      // FIX: producer emits `trigger` (e.g. "wait_call"), not `reason`.
      return activitySignal(
        payload.activity_type,
        "PARK",
        str(payload.detail.trigger, "actor parked"),
        ts,
      );
    }
    case "channel_lock_waiting": {
      return activitySignal(
        payload.activity_type,
        "LOCK",
        `waiting for #${str(payload.detail.channel_id, "channel")}`,
        ts,
      );
    }
    case "channel_lock_acquired": {
      const waitedMs = num(payload.detail.waited_ms);
      return activitySignal(
        payload.activity_type,
        "LOCK",
        waitedMs !== null
          ? `acquired after ${Math.round(waitedMs)}ms`
          : "acquired",
        ts,
      );
    }
    case "cache_break_detected": {
      // FIX: producer emits `delta_pp`, not `drop_pp`.
      const drop = num(payload.detail.delta_pp);
      return activitySignal(
        payload.activity_type,
        "CACHE",
        drop !== null ? `cache break -${drop.toFixed(1)}pp` : "cache break",
        ts,
      );
    }
    case "context_window": {
      const { detail } = payload;
      const growth = num(detail.context_growth_since_evolution);
      const currentPhase = str(detail.current_phase, "phase");
      return activitySignal(
        payload.activity_type,
        "CTX",
        growth !== null
          ? `${currentPhase} context ${Math.round(growth).toLocaleString()} tokens`
          : `${currentPhase} context updated`,
        ts,
      );
    }
    case "evolution_cycle": {
      const evolution = num(payload.detail.evolution);
      return activitySignal(
        payload.activity_type,
        "EVO",
        evolution !== null ? `evolved to level ${evolution}` : "evolved",
        ts,
      );
    }
    case "step_completed": {
      const { detail } = payload;
      const durationMs = num(detail.duration_ms);
      const stepKind = str(detail.step_kind, "step");
      return activitySignal(
        payload.activity_type,
        "STEP",
        durationMs !== null
          ? `${stepKind} completed in ${Math.round(durationMs)}ms`
          : `${stepKind} completed`,
        ts,
      );
    }
    case "consolidation_only_wake": {
      return activitySignal(
        payload.activity_type,
        "WAKE",
        str(payload.detail.trigger, "consolidation wake"),
        ts,
      );
    }
    case "external_compact_seed": {
      return activitySignal(
        payload.activity_type,
        "WAKE",
        str(payload.detail.trigger, "external compact"),
        ts,
      );
    }
    default: {
      // Exhaustive switch — the union has no more cases. This default is
      // unreachable at compile time but guards against runtime data that
      // doesn't match the schema (WS client does not zod-parse).
      return null;
    }
  }
}

export const __testing = { mapActivityToStreamEvent };

const EVENT_CAP = 200;

export function useInstanceStream(
  instanceId: string | null,
  channelIds?: string[],
): UseInstanceStreamResult {
  const [events, setEvents] = useState<IngestedStreamEvent[]>([]);
  const [eventsOverflowed, setEventsOverflowed] = useState(false);
  const [liveSince, setLiveSince] = useState<string | null>(null);
  const [messages, setMessages] = useState<ChannelMessage[]>([]);
  const { isHealthy, subscribe } = useDashboardRealtime();
  const instanceIdRef = useRef(instanceId);
  instanceIdRef.current = instanceId;
  const channelIdsRef = useRef(channelIds);
  channelIdsRef.current = channelIds;
  const ingestionSeqRef = useRef(0);

  // Reset state when instance changes.
  useEffect(() => {
    setEvents([]);
    setEventsOverflowed(false);
    setLiveSince(null);
    setMessages([]);
    ingestionSeqRef.current = 0;
  }, [instanceId]);

  // Subscribe to WS events filtered by instance.
  useEffect(() => {
    if (!instanceId) {
      return;
    }
    setLiveSince(new Date().toISOString());

    const unsubscribe = subscribe(
      (message) => {
        // Filter to instance_activity events for this instance.
        if (message.type !== "instance_activity") {
          return;
        }

        const { payload } = message;
        const eventInstanceId = payload.instance_id;

        // Extract channel_id for filtering — channel-bearing activity types.
        // cache_stats/token_delta/step_completed carry it when the node has
        // channel context (threaded in the prior commit); the lock + message
        // types always carry it.
        const eventChannelId =
          payload.activity_type === "channel_message" ||
          payload.activity_type === "channel_lock_waiting" ||
          payload.activity_type === "channel_lock_acquired" ||
          payload.activity_type === "cache_stats" ||
          payload.activity_type === "token_delta" ||
          payload.activity_type === "step_completed" ||
          payload.activity_type === "actor_wake" ||
          payload.activity_type === "actor_park" ||
          payload.activity_type === "status_change" ||
          payload.activity_type === "cache_break_detected" ||
          payload.activity_type === "context_window"
            ? str(payload.detail.channel_id)
            : null;
        const isKnownChannel =
          eventChannelId !== null &&
          (channelIdsRef.current?.includes(eventChannelId) ?? false);

        if (eventInstanceId !== instanceIdRef.current && !isKnownChannel) {
          return;
        }

        // Channel messages go to the Chat tab (messages state).
        if (payload.activity_type === "channel_message") {
          const { detail } = payload;
          const msg: ChannelMessage = {
            channelId: str(detail.channel_id),
            content: str(detail.content),
            createdAt: new Date(message.timestamp).toISOString(),
            id: str(detail.message_id) || crypto.randomUUID(),
            senderDisplayName: str(detail.sender_display_name) || null,
            senderId: str(detail.sender_id),
            senderType: detail.sender_type,
          };
          setMessages((prev) =>
            prev.some((existing) => existing.id === msg.id)
              ? prev
              : [...prev, msg],
          );
        }

        // All activity types also appear in the Stream tab.
        const streamEvent = mapActivityToStreamEvent(
          payload,
          message.timestamp,
        );
        if (streamEvent) {
          ingestionSeqRef.current += 1;
          const sourcedEvent = withEventSource(
            streamEvent,
            eventInstanceId,
            eventChannelId,
            ingestionSeqRef.current,
          );
          // Cap at EVENT_CAP events — drop the oldest when exceeded.
          setEvents((prev) => {
            const next = [...prev, sourcedEvent];
            if (next.length > EVENT_CAP) {
              setEventsOverflowed(true);
              return next.slice(next.length - EVENT_CAP);
            }
            return next;
          });
        }
      },
      { topic: "internalEventTopic" },
    );

    return unsubscribe;
  }, [instanceId, subscribe]);

  return {
    connected: isHealthy,
    events,
    eventsOverflowed,
    liveSince,
    messages,
  };
}
